import { useEffect, useRef, useState } from 'preact/hooks';
import { ApiError, request, type Session, describeError } from '../api/client';
import { newIdempotencyKey } from '../api/ids';
import { displayDate } from '../api/adapter';
import type { WireReflection } from '../api/wire';
import {
  UnreadableImage,
  deletePhoto,
  listPhotos,
  uploadPhoto,
  type Photo,
} from '../api/photos';
import { PhotoStrip, PhotoViewer } from '../components/composed/Photos';

type State =
  | { at: 'loading' }
  | { at: 'writing'; text: string; etag: string | null; revision: number }
  | { at: 'saving'; text: string }
  | { at: 'conflict'; theirs: WireReflection; mine: string }
  | { at: 'error'; message: string };

/** The one screen that drops the grid.
 *
 *  Sans prose at a comfortable measure, no glyph column, no counters beyond a
 *  quiet word count. Everything else in the product is a log; this is writing.
 *
 *  `If-Match` is required only once a reflection exists, and the validator is a
 *  synthesized `"reflection:{date}:{revision}"` rather than a content hash — so
 *  it must come from the GET rather than be constructed here.
 */
export function ReflectionEditor({
  session,
  journalDate,
  onSaved,
  onChanged,
  onDismiss,
}: {
  session: Session;
  journalDate: string;
  onSaved: () => void;
  /** A photograph was attached or removed. Separate from `onSaved` because it
   *  neither closes the editor nor commits any prose — the day behind the sheet
   *  just stopped being accurate, and closing without refreshing it would show
   *  a day that visibly lacks the photograph just added to it. */
  onChanged: () => void;
  onDismiss: () => void;
}) {
  const [state, setState] = useState<State>({ at: 'loading' });
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [viewing, setViewing] = useState<Photo | null>(null);
  /** Upload progress and failure, kept apart from the reflection's own state:
   *  a photograph that fails to upload must not discard unsaved prose. */
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  /** `ALSO ON THIS DAY` — what the day held, beside what you wrote about it.
   *  Fetched here rather than threaded through App, which does not hold the day.
   *  Optional: a reflection is writable whether or not these load. */
  const [alsoToday, setAlsoToday] = useState<{
    done: number;
    open: number;
    habitsHit: number;
    habits: number;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [day, tracker] = await Promise.all([
          request<{ entries: { kind: string; state: string }[] }>(
            session,
            `/v1/days/${journalDate}`,
          ),
          request<{ items: { goal_per_day: number; days: { count: number }[] }[] }>(
            session,
            `/v1/habits/tracker?from=${journalDate}&to=${journalDate}`,
          ).catch(() => null),
        ]);
        if (cancelled) return;
        const entries = day.body.entries ?? [];
        const habits = tracker?.body.items ?? [];
        setAlsoToday({
          done: entries.filter((e) => e.state === 'done').length,
          open: entries.filter((e) => e.state === 'open').length,
          habitsHit: habits.filter((h) => {
            const count = h.days[0]?.count ?? 0;
            return count > 0 && count >= (h.goal_per_day || 1);
          }).length,
          habits: habits.length,
        });
      } catch {
        // The day being unreadable must not stop someone writing about it.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, journalDate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { body, etag } = await request<WireReflection>(
          session,
          `/v1/days/${journalDate}/reflection`,
        );
        if (!cancelled) {
          setState({ at: 'writing', text: body.content, etag, revision: body.revision });
        }
      } catch (error) {
        // 404 is the ordinary case: nothing written yet, and no validator needed.
        if (error instanceof ApiError && error.status === 404) {
          if (!cancelled) setState({ at: 'writing', text: '', etag: null, revision: 0 });
          return;
        }
        if (!cancelled) {
          setState({ at: 'error', message: describe(error) });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, journalDate]);

  useEffect(() => {
    let cancelled = false;
    listPhotos(session, journalDate)
      .then((items) => {
        if (!cancelled) setPhotos(items);
      })
      .catch(() => {
        // A day with no reflection yet has no photographs either, and that is
        // not a failure worth a banner.
      });
    return () => {
      cancelled = true;
    };
  }, [session, journalDate]);

  async function attach(file: File) {
    setPhotoBusy(true);
    setPhotoError(null);
    try {
      const photo = await uploadPhoto(session, journalDate, file);
      // Appended rather than refetched: the list is ordered by when each was
      // added, so the local order already matches the server's.
      setPhotos((current) => [...current, photo]);
      onChanged();
    } catch (error) {
      setPhotoError(
        error instanceof UnreadableImage ? error.message : describe(error),
      );
    } finally {
      setPhotoBusy(false);
    }
  }

  async function detach(photo: Photo) {
    setPhotoError(null);
    try {
      await deletePhoto(session, photo.id);
      setPhotos((current) => current.filter((p) => p.id !== photo.id));
      setViewing(null);
      onChanged();
    } catch (error) {
      setPhotoError(describe(error));
    }
  }

  async function commit(text: string, etag: string | null) {
    setState({ at: 'saving', text });
    try {
      await request(session, `/v1/days/${journalDate}/reflection`, {
        method: 'PUT',
        etag,
        idempotencyKey: newIdempotencyKey(),
        body: { content: text, status: 'committed', provenance: 'typed' },
      });
      onSaved();
    } catch (error) {
      if (error instanceof ApiError && error.isRevisionConflict) {
        // Show both and let a person choose. Never merge prose.
        const theirs = (
          await request<WireReflection>(session, `/v1/days/${journalDate}/reflection`)
        ).body;
        setState({ at: 'conflict', theirs, mine: text });
        return;
      }
      setState({ at: 'error', message: describe(error) });
    }
  }

  return (
    <div style={sheet}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={label}>REFLECTION · {displayDate(journalDate)}</span>
        <button type="button" onClick={onDismiss} style={{ ...label, color: 'var(--bujo-text-meta)', minHeight: 'var(--bujo-row-min)', cursor: 'pointer' }}>
          CLOSE
        </button>
      </header>

      {state.at === 'loading' ? <Prose muted>Loading…</Prose> : null}
      {state.at === 'saving' ? <Prose muted>Saving…</Prose> : null}

      {state.at === 'error' ? (
        <>
          <Prose tone="error">{state.message}</Prose>
          <Prose muted>Nothing was saved.</Prose>
        </>
      ) : null}

      {state.at === 'writing' ? (
        <>
          <textarea
            value={state.text}
            onInput={(e) =>
              setState({ ...state, text: (e.target as HTMLTextAreaElement).value })
            }
            placeholder="How was today?"
            autocapitalize="sentences"
            spellcheck
            rows={10}
            style={{
              flex: 1,
              minHeight: 220,
              width: '100%',
              resize: 'none',
              background: 'transparent',
              border: 0,
              borderRadius: 0,
              color: 'var(--bujo-text-primary)',
              // Sans, and a real measure. This is the only place in the product
              // that is prose rather than a log line.
              font: 'var(--bujo-fw-regular) 16px/1.75 var(--bujo-font-sans)',
            }}
          />
          {alsoToday ? (
            <div>
              <span style={label}>ALSO ON THIS DAY</span>
              <div style={{ ...quiet, paddingTop: 'var(--bujo-space-2)' }}>
                {alsoToday.done} completed · {alsoToday.open} still open
                {alsoToday.habits > 0
                  ? ` · ${alsoToday.habitsHit}/${alsoToday.habits} habits hit`
                  : ''}
              </div>
            </div>
          ) : null}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={label}>PHOTOGRAPHS</span>
              <span style={quiet}>
                {photos.length > 0 ? `${photos.length} · TAP TO OPEN` : 'NONE'}
              </span>
            </div>
            <PhotoStrip session={session} photos={photos} onOpen={setViewing} />
            {/* `accept` rather than `capture`: `capture` forces the camera and
                takes away the library, and most of what gets attached to a
                reflection was taken earlier in the day. */}
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const input = e.target as HTMLInputElement;
                const file = input.files?.[0];
                // Cleared before the upload so choosing the same file twice
                // still fires a change event.
                input.value = '';
                if (file) void attach(file);
              }}
            />
            <button
              type="button"
              disabled={photoBusy}
              onClick={() => fileInput.current?.click()}
              style={{
                marginTop: 'var(--bujo-space-2)',
                minHeight: 'var(--bujo-tap-min)',
                width: '100%',
                border: '1px solid var(--bujo-rule-hairline)',
                color: photoBusy ? 'var(--bujo-text-disabled)' : 'var(--bujo-text-meta)',
                font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
                letterSpacing: 'var(--bujo-ls-label)',
                cursor: photoBusy ? 'default' : 'pointer',
              }}
            >
              {photoBusy ? 'UPLOADING…' : '+ PHOTOGRAPH'}
            </button>
            {photoError ? (
              <p style={{ ...quiet, color: 'var(--bujo-state-error)', margin: 0, paddingTop: 'var(--bujo-space-2)' }}>
                {photoError}
              </p>
            ) : null}
            {/* Said once, here, rather than on every upload. Two facts someone
                needs before they attach the first one, and never again. */}
            <p style={{ ...quiet, margin: 0, paddingTop: 'var(--bujo-space-2)', lineHeight: 1.5 }}>
              Resized on this device before upload, which removes location and
              camera data. Photographs need a connection — they are not queued
              offline.
            </p>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              borderTop: '1px solid var(--bujo-rule-hairline)',
              paddingTop: 'var(--bujo-space-3)',
            }}
          >
            <span style={quiet}>{words(state.text)} WORDS</span>
            {/* The reference says `SAVES ON CLOSE`. This commits explicitly
                instead, because a reflection is `If-Match` guarded and a save
                triggered by leaving would surface its 412 exactly when someone
                is trying to leave. The label states what actually happens. */}
            <span style={quiet}>
              {state.revision > 0 ? `REVISION ${state.revision} · ` : ''}COMMIT TO SAVE
            </span>
          </div>
          <button
            type="button"
            disabled={!state.text.trim()}
            onClick={() => void commit(state.text.trim(), state.etag)}
            style={{
              minHeight: 'var(--bujo-tap-min)',
              border: `1px solid ${state.text.trim() ? 'var(--bujo-accent)' : 'var(--bujo-rule-hairline)'}`,
              color: state.text.trim() ? 'var(--bujo-accent)' : 'var(--bujo-text-disabled)',
              font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
              letterSpacing: 'var(--bujo-ls-label)',
              cursor: state.text.trim() ? 'pointer' : 'default',
            }}
          >
            COMMIT
          </button>
        </>
      ) : null}

      {viewing ? (
        <PhotoViewer
          session={session}
          photo={viewing}
          onDismiss={() => setViewing(null)}
          onRemove={() => void detach(viewing)}
        />
      ) : null}

      {state.at === 'conflict' ? (
        <>
          <Prose tone="error">CONFLICT · 412 · reflection changed elsewhere</Prose>
          <Prose muted>
            This day's reflection was rewritten by another client. Both versions are
            here; nothing was discarded.
          </Prose>
          <span style={label}>ON THE SERVER NOW</span>
          <Prose>{state.theirs.content}</Prose>
          <span style={label}>WHAT YOU WROTE</span>
          <Prose>{state.mine}</Prose>
          <button
            type="button"
            onClick={() =>
              void commit(state.mine, `"reflection:${journalDate}:${state.theirs.revision}"`)
            }
            style={{
              minHeight: 'var(--bujo-tap-min)',
              border: '1px solid var(--bujo-accent)',
              color: 'var(--bujo-accent)',
              font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
              letterSpacing: 'var(--bujo-ls-label)',
              cursor: 'pointer',
            }}
          >
            KEEP MINE · replaces theirs
          </button>
          <button type="button" onClick={onDismiss} style={{ ...quiet, minHeight: 'var(--bujo-tap-min)', cursor: 'pointer' }}>
            keep theirs · discards mine
          </button>
        </>
      ) : null}
    </div>
  );
}

function words(text: string): number {
  const t = text.trim();
  return t ? t.split(/\s+/).length : 0;
}

function describe(error: unknown): string {
  if (error instanceof ApiError) return describeError(error);
  return String(error);
}

function Prose({ children, muted, tone }: { children: unknown; muted?: boolean; tone?: 'error' }) {
  return (
    <p
      style={{
        margin: 0,
        font: 'var(--bujo-fw-regular) var(--bujo-fs-body)/var(--bujo-lh-prose) var(--bujo-font-sans)',
        color:
          tone === 'error'
            ? 'var(--bujo-state-error)'
            : muted
              ? 'var(--bujo-text-meta)'
              : 'var(--bujo-text-primary)',
      }}
    >
      {children as never}
    </p>
  );
}

const sheet = {
  position: 'fixed' as const,
  inset: 0,
  zIndex: 40,
  background: 'var(--bujo-surface-page)',
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 'var(--bujo-space-4)',
  padding: 'var(--bujo-gutter)',
  paddingTop: 'calc(var(--bujo-gutter) + env(safe-area-inset-top))',
  paddingLeft: 'calc(var(--bujo-gutter) + env(safe-area-inset-left))',
  paddingRight: 'calc(var(--bujo-gutter) + env(safe-area-inset-right))',
  paddingBottom: 'calc(var(--bujo-gutter) + env(safe-area-inset-bottom))',
  overflowY: 'auto' as const,
};

const label = {
  font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-label)',
  color: 'var(--bujo-label)',
};

const quiet = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1 var(--bujo-font-mono)',
  color: 'var(--bujo-text-meta)',
};
